<div *ngIf="organization">
  <div class="row">
    <!-- Organization Information Card -->
    <div class="col-lg-6 mb-4">
      <div class="card border-0 bg-transparent shadow-none">
        <div class="card-header bg-transparent px-0">
          <h6 class="mb-0">
            <i class="bi bi-building me-2"></i>
            Organization Information
          </h6>
        </div>
        <div class="card-body px-0 list-group list-group-flush">
          <div class="list-group-item bg-transparent px-0 py-3">
            <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-2">Name</label>
            <div>{{ organization.name }}</div>
          </div>
          <div class="list-group-item bg-transparent px-0 py-3">
            <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-2">Type</label>
            <div>
              <span class="badge bg-primary">
                {{ getOrganizationTypeLabel(organization.organizationType) }}
              </span>
            </div>
          </div>
          <div class="list-group-item bg-transparent px-0 py-3" *ngIf="organization.ein">
            <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-2">EIN</label>
            <div>{{ maskEIN(organization.ein) }}</div>
          </div>
          <div class="list-group-item bg-transparent px-0 py-3">
            <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-2">Address</label>
            <div>
              <div>{{ organization.streetAddressLine1 }}</div>
              <div *ngIf="organization.streetAddressLine2">{{ organization.streetAddressLine2 }}</div>
              <div>{{ organization.city }}, {{ organization.state }} {{ organization.zipCode }}</div>
              <div>{{ organization.country }}</div>
            </div>
          </div>
          <!-- Reads from the new adn_organization_payment_accounts via the
               enriched admin endpoint (billing-payments § 2.1). Replaces
               the deprecated organization.stripeCustomerId column read.
               `as customer` / `as connect` aliasing narrows the type so
               the inner template can drop the !-chains. -->
          <ng-container *ngIf="organization.paymentAccounts?.customer as customer">
            <div class="list-group-item bg-transparent px-0 py-3" *ngIf="customer.stripeCustomerId">
              <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-2">Stripe Customer ID</label>
              <div>
                <code class="small">{{ customer.stripeCustomerId }}</code>
                <div class="text-muted small mt-1">
                  Status: {{ customer.status }}
                  <span *ngIf="customer.defaultPaymentMethodId">
                    &nbsp;·&nbsp; Default PM on file
                  </span>
                  <span *ngIf="customer.subscriptionStatus">
                    &nbsp;·&nbsp; Subscription: {{ customer.subscriptionStatus }}
                  </span>
                </div>
              </div>
            </div>
          </ng-container>
          <!-- Deprecation-window fallback: if the response predates the
               adn_organization_payment_accounts enrichment, fall back to
               the (deprecated) organization.stripeCustomerId column so
               admins can still see the customer id. Delete this block
               when the deprecated column is dropped. -->
          <div
            class="list-group-item bg-transparent px-0 py-3"
            *ngIf="!organization.paymentAccounts?.customer && organization.stripeCustomerId"
          >
            <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-2">Stripe Customer ID</label>
            <div>
              <code class="small">{{ organization.stripeCustomerId }}</code>
              <div class="text-muted small mt-1">
                <em>Legacy field — Hub has not yet been upgraded to return paymentAccounts.</em>
              </div>
            </div>
          </div>
          <ng-container *ngIf="organization.paymentAccounts?.connect as connect">
            <div class="list-group-item bg-transparent px-0 py-3" *ngIf="connect.stripeAccountId">
              <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-2">Stripe Connect Account</label>
              <div>
                <code class="small">{{ connect.stripeAccountId }}</code>
                <div class="text-muted small mt-1">
                  Status: {{ connect.status }}
                  &nbsp;·&nbsp;
                  Charges: {{ connect.chargesEnabled ? 'on' : 'off' }}
                  &nbsp;·&nbsp;
                  Payouts: {{ connect.payoutsEnabled ? 'on' : 'off' }}
                </div>
              </div>
            </div>
          </ng-container>
          <div class="list-group-item bg-transparent px-0 py-3" *ngIf="organization.zohoCompanyId">
            <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-2">Zoho Company ID</label>
            <div>
              <code class="small">{{ organization.zohoCompanyId }}</code>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Membership Card (ADN-2513) -->
    <div class="col-lg-6 mb-4">
      <app-organization-membership-card
        [organizationId]="organization.id"
        [organizationName]="organization.name"
        [membership]="organization.membership ?? null"
        (changed)="membershipChanged.emit($event)"
      ></app-organization-membership-card>
    </div>

    <!-- Leadership Card -->
    <div class="col-lg-6 mb-4">
      <div class="card border-0 bg-transparent shadow-none">
        <div class="card-header bg-transparent px-0">
          <h6 class="mb-0">
            <i class="bi bi-people-fill me-2"></i>
            Leadership
          </h6>
        </div>
        <div class="card-body px-0 list-group list-group-flush">
          <div class="list-group-item bg-transparent px-0 py-3">
            <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-2">Owner</label>
            <div *ngIf="ownerProfile">
              <div class="d-flex align-items-center">
                <div class="entity-avatar me-2">
                  {{ ownerProfile.firstName.charAt(0) }}{{ ownerProfile.lastName.charAt(0) }}
                </div>
                <div>
                  <div class="fw-medium">{{ ownerProfile.firstName }} {{ ownerProfile.lastName }}</div>
                  <div class="text-muted small">{{ ownerProfile.email }}</div>
                </div>
              </div>
            </div>
            <div *ngIf="!ownerProfile" class="text-muted">
              Not assigned
            </div>
          </div>
          <div class="list-group-item bg-transparent px-0 py-3">
            <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-2">Chief Supervisor</label>
            <div *ngIf="chiefSupervisorProfile">
              <div class="d-flex align-items-center">
                <div class="entity-avatar me-2">
                  {{ chiefSupervisorProfile.firstName.charAt(0) }}{{ chiefSupervisorProfile.lastName.charAt(0) }}
                </div>
                <div>
                  <div class="fw-medium">{{ chiefSupervisorProfile.firstName }} {{ chiefSupervisorProfile.lastName }}</div>
                  <div class="text-muted small">{{ chiefSupervisorProfile.email }}</div>
                </div>
              </div>
            </div>
            <div *ngIf="!chiefSupervisorProfile" class="text-muted">
              Not assigned
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Stats Card -->
    <div class="col-12">
      <div class="card border-0 bg-transparent shadow-none">
        <div class="card-header bg-transparent px-0">
          <h6 class="mb-0">
            <i class="bi bi-bar-chart-line me-2"></i>
            Quick Stats
          </h6>
        </div>
        <div class="card-body px-0">
          <div class="row">
            <!-- Members Stats -->
            <div class="col-md-3 mb-3">
              <div class="stat-card d-flex align-items-start gap-3 p-2">
                <div class="stat-icon bg-primary bg-opacity-10 text-primary">
                  <i class="bi bi-people"></i>
                </div>
                <div class="flex-grow-1">
                  <div class="small fw-semibold text-uppercase text-body-secondary mb-1">Members</div>
                  <div class="fs-5 fw-semibold mb-1">{{ stats?.activeMembers || 0 }} active</div>
                  <div class="small text-muted" *ngIf="stats && stats.inactiveMembers > 0">
                    {{ stats.inactiveMembers }} inactive
                  </div>
                </div>
              </div>
            </div>

            <!-- Drones Stats -->
            <div class="col-md-3 mb-3">
              <div class="stat-card d-flex align-items-start gap-3 p-2">
                <div class="stat-icon bg-info bg-opacity-10 text-info">
                  <i class="bi bi-truck"></i>
                </div>
                <div class="flex-grow-1">
                  <div class="small fw-semibold text-uppercase text-body-secondary mb-1">Drones</div>
                  <div class="fs-5 fw-semibold mb-1">{{ stats?.activeDrones || 0 }} active</div>
                  <div class="small text-muted" *ngIf="stats && stats.inactiveDrones > 0">
                    {{ stats.inactiveDrones }} inactive
                  </div>
                </div>
              </div>
            </div>

            <!-- Certifications Stats -->
            <div class="col-md-3 mb-3">
              <div class="stat-card d-flex align-items-start gap-3 p-2">
                <div class="stat-icon bg-success bg-opacity-10 text-success">
                  <i class="bi bi-patch-check"></i>
                </div>
                <div class="flex-grow-1">
                  <div class="small fw-semibold text-uppercase text-body-secondary mb-1">Certifications</div>
                  <div class="fs-5 fw-semibold mb-1">{{ stats?.validCertifications || 0 }} valid</div>
                  <div class="small" *ngIf="stats && stats.expiringSoonCertifications > 0">
                    <span class="text-warning">{{ stats.expiringSoonCertifications }} expiring soon</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Last Activity Stats -->
            <div class="col-md-3 mb-3">
              <div class="stat-card d-flex align-items-start gap-3 p-2">
                <div class="stat-icon bg-secondary bg-opacity-10 text-secondary">
                  <i class="bi bi-clock-history"></i>
                </div>
                <div class="flex-grow-1">
                  <div class="small fw-semibold text-uppercase text-body-secondary mb-1">Last Activity</div>
                  <div class="fs-5 fw-semibold mb-1">{{ formatTimeAgo(stats?.lastActivityAt || null) }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
